/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import styled from 'styled-components';
import _ from 'lodash';

import { FiArrowRight } from 'react-icons/fi';
import { MdCheckCircle, MdDriveFileRenameOutline } from 'react-icons/md';
import { AiOutlineSave } from 'react-icons/ai';

import { Button } from '../Button';
import { InputComponent } from '../form/InputComponent';
import { Logo } from '../logo/logo';
import Spinner from '../spinner/Spinner';
import { Infos } from '../infos/Infos';

import useDidUpdate from 'src/hooks/useDidUpdate';
import { paths } from 'src/routes/routes.constants';
import { checkSubContractorSurveyStatus, fetchContentfulEntryById } from '../../utils/api';
import { BUTTON_COLORS, COLORS } from 'src/utils/constants';
import { setSessionValue } from 'src/utils/storage';
import { compareObjects } from 'src/utils/utils';
export interface WizardSlide {
  step: number;
  label: string;
  title?: string;
  content?: string;
  input?: boolean;
  inputType?: string;
  options?: Array<any>;
  buttonLabel?: string;
  conditions?: Array<any>;
  hasQuestion?: boolean;
  showNextButton: boolean;
  buttonShouldSave?: boolean;
  items?: Array<any>;
  saveFunction?: (params: {
    accountId: any;
    actionId: any;
    relatedProductId: any;
    versionProductId: any;
    responses: any;
    entryId: any;
    subContractorName: any;
    isSurveyCompleted: boolean;
  }) => Promise<any>;
}

export interface WizardProps {
  slideList: Array<WizardSlide>;
}

const WizardSubContractor = ({ slideList }: WizardProps) => {
  const history = useHistory();
  const params: { accountId; entityId; actionId; relatedProductId; versionProductId; subContractorName; entryId } = useParams();

  const containerRef = useRef(null);

  const [questionData, setQuestionData] = useState([]);
  const [currentStep, setCurrentStep] = useState(null);
  const [currentStepSurvey, setCurrentStepSurvey] = useState(null);

  const [values, setValues] = useState([]);
  const [error, setError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const [responses, setResponses] = useState({});
  const [dbResponses, setDBResponses] = useState({});
  const [displayedQuestion, setDisplayedQuestion] = useState([]);
  const [initialRender, setInitialRender] = useState(true);
  const [companyName, setCompanyName] = useState<string>('');

  useEffect(() => {
    let isSurveyAnswered: boolean = false;
    let showSurveySlide: boolean;
    const { accountId, entityId, actionId, entryId, versionProductId, relatedProductId, subContractorName } = params;

    (async () => {
      try {
        setLoading(true);
        if (accountId && actionId) {
          const result = await checkSubContractorSurveyStatus(accountId, entityId, actionId, versionProductId, relatedProductId, subContractorName);
          if (!result) {
            throw new Error();
          }
          showSurveySlide = result?.showSurvey;
          isSurveyAnswered = result?.surveyStatus;
          setCompanyName(result?.companyName);
          setResponses(result?.responses);
          setDBResponses(result?.responses);
          if (result?.tempToken) setSessionValue('tempToken', result.tempToken);
        }
        if (isSurveyAnswered) {
          setCurrentStep(slideList.length);
        }
        if (!isSurveyAnswered && entryId) {
          const result = await fetchContentfulEntryById(entryId);
          setQuestionData(result?.steps);
          if (showSurveySlide) {
            setCurrentStepSurvey({ id: result?.steps[0].id, name: result?.steps[0].name });
            setCurrentStep(3);
          } else setCurrentStepSurvey({ id: result?.steps[0].id, name: result?.steps[0].name });
        }
        setLoading(false);
      } catch (error) {
        setError(true);
        setLoading(false);
      }
    })();
  }, []);

  const setWizardCompleted = async () => {
    history.push(paths.dashboardGroup);
  };

  const goToStep = async nextStep => {
    let lastStep = nextStep > slideList.length;
    if (lastStep) {
      await setWizardCompleted();
      return;
    } else {
      setCurrentStep(nextStep);
    }
  };

  const isStepConditionsOK = step => {
    if (!step?.conditions?.length) {
      return true;
    }
    let conditions = step.conditions.every(condition => {
      if (values.find(value => value.name === `step-${condition.step}` && value.value === condition.value)) {
        return true;
      } else {
        return false;
      }
    });
    return conditions;
  };

  const isSelected = step => {
    return step === currentStep;
  };

  const isStepValid = step => {
    let hasInput = slideList[step - 1].input;
    let hasInputs = slideList[step - 1].items;
    let isEmpty = values.find(value => value.name === `step-${step}`) === undefined;
    let isVisible = isStepConditionsOK(slideList.find(item => item.step === step));

    if (hasInputs?.length) {
      return hasInputs?.every(
        item =>
          responses?.hasOwnProperty(item.name) && responses[item.name] !== '' && responses[item.name] !== null && responses[item.name] !== undefined
      );
    }
    if (slideList[step - 1].hasQuestion) {
      return isSurveyCompleted();
    }
    return !hasInput || !isEmpty || !isVisible;
  };

  const isSelectable = targetStep => {
    if (targetStep === 1 || currentStep > targetStep || !isStepValid(targetStep - 1)) {
      return false;
    }
    return true;
  };

  const handleStepChange = async targetStep => {
    if (isSelectable(targetStep)) {
      setError(false);
      await goToStep(targetStep);
    } else {
      setError(true);
    }
  };

  const handleSetValue = event => {
    let newValues = [...values];
    let stepCopy = newValues.find(value => value.name === event.target.name);
    if (!stepCopy) {
      newValues.push({ name: event.target.name, value: event.target.value });
    } else {
      if (event.target.value === '') {
        newValues = newValues.filter(value => value.name !== event.target.name);
      } else {
        stepCopy.value = event.target.value;
      }
    }
    setValues(newValues);
  };

  const handleSelect = (name, value) => {
    let newValues = [...values];
    let stepCopy = newValues.find(value => value.name === name);
    if (!stepCopy) {
      newValues.push({ name, value });
    } else {
      if (value === '') {
        newValues = newValues.filter(value => value.name !== name);
      } else {
        stepCopy.value = value;
      }
    }
    setValues(newValues);
  };

  const getNextStep = () => {
    for (let step = currentStep; step < slideList.length; step++) {
      if (isStepConditionsOK(slideList[step])) return step + 1;
    }
    return currentStep + 1;
  };

  const getStepValue = step => {
    //TODO check default value for select input type
    return values.find(value => value.name === `step-${step}`)?.value;
  };

  const handleChangeResponses = event => {
    const { name, value } = event.target;
    setResponses(prevState => {
      if (value === null) {
        delete prevState[name];
        return {
          ...prevState,
        };
      }
      return {
        ...prevState,
        [name]: value,
      };
    });
  };

  const isQuestionConditionOK = (question, addToDisplayedQuestion = false) => {
    let isQuestionInList = false;
    if (addToDisplayedQuestion) {
      isQuestionInList = !!displayedQuestion.find(item => item.id === question.id);
    }

    if (!question?.conditions?.length) {
      if (addToDisplayedQuestion) {
        if (isQuestionInList) return true;
        setDisplayedQuestion(prevState => [...prevState, question]);
      }
      return true;
    }

    const shouldDisplay = question.conditions.every(condition => {
      const currentResponse = responses ? responses[condition.key] : undefined;

      if (currentResponse === undefined || currentResponse === null) return false;

      const meetsCondition = Array.isArray(currentResponse) ? currentResponse.includes(condition.value) : currentResponse === condition.value;

      if (addToDisplayedQuestion) {
        if (meetsCondition && !isQuestionInList) {
          setDisplayedQuestion(prevState => [...prevState, question]);
        } else if (!meetsCondition && isQuestionInList) {
          const filteredData = displayedQuestion.filter(item => item.id !== question.id);
          setDisplayedQuestion(filteredData);
        }
      }

      return meetsCondition;
    });

    return shouldDisplay;
  };

  // --------------------------

  const handleInputChange = event => {
    const { name, value } = event.target;
    setResponses(prevInput => ({
      ...prevInput,
      [name]: value,
    }));
  };

  const handleButtonClick = async (slideStep: WizardSlide, justSave: boolean = false) => {
    if (slideStep.buttonShouldSave) {
      setLoading(true);
      const { accountId, actionId, relatedProductId, versionProductId, entryId, subContractorName } = params;
      slideStep
        .saveFunction({
          accountId,
          actionId,
          relatedProductId,
          versionProductId,
          responses,
          entryId,
          subContractorName,
          isSurveyCompleted: isSurveyCompleted(),
        })
        .then(res => {
          setLoading(false);
          if (res.success) {
            if (justSave) {
              setDBResponses(responses);
            } else {
              handleStepChange(getNextStep());
            }
          }
        })
        .catch(err => {
          setLoading(false);
        });
    } else {
      handleStepChange(getNextStep());
    }
  };

  const scrollToNewElement = () => {
    const scrollableDiv = containerRef.current;
    if (scrollableDiv) {
      const newElement = scrollableDiv.lastElementChild;
      if (newElement) {
        const newElementRect = newElement.getBoundingClientRect();
        const scrollableDivRect = scrollableDiv.getBoundingClientRect();
        if (newElementRect.bottom > scrollableDivRect.bottom || newElementRect.top < scrollableDivRect.top) {
          newElement.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  };

  useDidUpdate(() => {
    if (initialRender) {
      setInitialRender(false);
      return;
    }
    scrollToNewElement();
  }, [displayedQuestion]);
  // --------------------------

  const isSurveyCompleted = () => {
    return (
      questionData.every(step => {
        return isSurveyStepCompleted(step.id);
      }) &&
      displayedQuestion.every(question => {
        return responses[question.name];
      })
    );
  };

  const isSurveyStepCompleted = stepId => {
    let stepQuestions = getSurveyStepQuestions(stepId);
    return stepQuestions.every(question => {
      if (question.required) return !!responses?.[question.name];
      return true;
    });
  };

  const getSurveyStepQuestions = stepId => {
    return questionData
      ?.find(step => step.id === stepId)
      ?.form.map(question => {
        return {
          name: question.name,
          id: question.id,
          required: question.required,
        };
      });
  };

  const isSurveyStepEmpty = stepId => {
    return getSurveyStepQuestions(stepId)?.length === 0;
  };

  const getStepIcon = step => {
    return isSurveyStepCompleted(step.id) ? <MdCheckCircle /> : isSurveyStepEmpty(step.id) ? <Circle /> : <MdDriveFileRenameOutline />;
  };

  const isResponsesChanged = useMemo(() => {
    const object1 = _.omit(dbResponses, ['responsableEmail', 'responsablePosition', 'companyName']);
    const object2 = _.omit(responses, ['responsableEmail', 'responsablePosition', 'companyName']);
    const isThereChanges = compareObjects(object1, object2);
    return isThereChanges;
  }, [responses, dbResponses]);

  return (
    <WizardContainer>
      <SideBar>
        <LogoContainer>
          <Logo whiteBg={false} />
          <Separator />
        </LogoContainer>

        <StepContainer>
          {slideList.map((item, idx) => {
            return (
              isStepConditionsOK(item) && (
                <Step key={idx} selected={isSelected(item.step)} isSelectable={currentStep === 1 ? false : isSelectable(item.step)}>
                  <FiArrowRight />
                  {item.label}
                </Step>
              )
            );
          })}
        </StepContainer>
      </SideBar>
      <ContentContainer>
        {slideList.map((item, index) => {
          return (
            isStepConditionsOK(item) &&
            item.step === currentStep && (
              <Slide key={index} slide={item.step}>
                {item?.title && <StepTitle>{item.title}</StepTitle>}
                {item?.content && <Content>{item.content.replace('{companyName}', companyName)}</Content>}
                {item?.input && (
                  <InputComponent
                    name={`step-${item.step}`}
                    type={item.inputType}
                    sharable={false}
                    value={getStepValue(item.step)}
                    onChange={item.inputType === 'select' ? handleSelect : handleSetValue}
                    options={item.options}
                  />
                )}
                {item?.items?.map((elt, index) => {
                  return (
                    <InputComponent
                      key={index}
                      name={elt.name}
                      type={elt.inputType}
                      sharable={false}
                      value={responses[elt.name]}
                      onChange={item.inputType === 'select' ? handleSelect : handleInputChange}
                      options={item.options}
                      label={elt.inputLabel}
                    />
                  );
                })}
                {item.hasQuestion && (
                  <SurveyPageContainer>
                    <ThemeContainer>
                      <ThemeList>
                        <ThemeTitle>Thèmes</ThemeTitle>
                        <ListContainer>
                          {questionData?.map(step => {
                            return (
                              <Theme
                                key={step.id}
                                selected={step.id === currentStepSurvey?.id}
                                onClick={() => setCurrentStepSurvey({ id: step.id, name: step.name })}
                                completed={isSurveyStepCompleted(step.id)}>
                                {getStepIcon(step)}
                                <span>{step?.name}</span>
                              </Theme>
                            );
                          })}
                        </ListContainer>
                        {isResponsesChanged && !isSurveyCompleted() && (
                          <StyledInfos
                            action={{ text: 'Sauvegarder mes réponses', onClick: () => handleButtonClick(item, true) }}
                            icon={AiOutlineSave}
                            text={'Vous avez des <b>modifications en cours</b>, pensez à <b>sauvegarder vos réponses</b> :)'}
                          />
                        )}
                      </ThemeList>
                    </ThemeContainer>
                    <SurveyContainer>
                      <SurveyTop>
                        <TitleContainer>
                          <Title>{currentStepSurvey?.name}</Title>
                        </TitleContainer>
                      </SurveyTop>
                      <QuestionsContainer>
                        {questionData
                          ?.find(step => step.id === currentStepSurvey?.id)
                          ?.form?.map((question, index) => {
                            return (
                              isQuestionConditionOK(question, true) && (
                                <InputComponent
                                  index={index}
                                  key={question.name + index}
                                  id={question.id}
                                  label={question.label}
                                  name={question.name}
                                  type={question.type}
                                  longAnswers={question.long}
                                  placeholder={question.placeholder}
                                  options={question?.options}
                                  value={responses[question.name]}
                                  onChange={handleChangeResponses}
                                  display={'true'}
                                  required={question.required}
                                  status={true}
                                  isSubQuestion={question.conditions?.length > 0}
                                  sharable={false}
                                  {...(question.type === 'upload' && {
                                    externalUpload: {
                                      accountId: params.accountId,
                                      entityId: params.entityId,
                                      path: `Audits/${params.subContractorName}`,
                                    },
                                  })}
                                />
                              )
                            );
                          })}
                      </QuestionsContainer>
                    </SurveyContainer>
                  </SurveyPageContainer>
                )}
                {item.showNextButton &&
                  (loading ? (
                    <SpinnerContainer slide={item.step}>
                      <Spinner color={BUTTON_COLORS.default} />
                    </SpinnerContainer>
                  ) : (
                    <ActionButton
                      label={item.buttonLabel}
                      slide={item.step}
                      onClick={() => handleButtonClick(item)}
                      disabled={!isStepValid(item.step) || error}
                    />
                  ))}
                {item.step === currentStep && error && <ErrorMessage>Une erreur est survenue. Veuillez réessayer ultérieurement.</ErrorMessage>}
              </Slide>
            )
          );
        })}
      </ContentContainer>
    </WizardContainer>
  );
};

export default WizardSubContractor;

const StyledInfos = styled(Infos)`
  position: relative;
  bottom: 100px;
`;

const ActionButton = styled(Button)<{ slide: number }>`
  ${({ slide }) =>
    slide === 3 &&
    `
    position: absolute;
    bottom: 5%;
    left: 410px;
    `}
`;

const WizardContainer = styled.div`
  height: 100vh;
  width: 100vw;
  top: 0;
  left: 0;
  display: flex;
  background-color: white;
`;

const SideBar = styled.div`
  width: 250px;
  background-color: #326c82;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 30px;
`;

const LogoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const Separator = styled.div`
  color: white;
  width: 50px;
  border: 1px solid white;
  margin-top: 35px;
`;

const StepContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 20px;
  font-family: 'Roboto';
  font-size: 1.33rem;
  margin-top: 50px;
  flex: 1;
  user-select: none;
`;

const Step = styled.div<{ selected: boolean; isSelectable: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  column-gap: 10px;
  color: white;
  font-weight: 400;
  cursor: ${({ isSelectable }) => (isSelectable ? 'pointer' : 'not-allowed')};

  svg {
    visibility: ${({ selected }) => (selected ? 'visible' : 'hidden')};
    height: 24px;
    width: 24px;
  }
`;

const ContentContainer = styled.div`
  overflow: hidden;
  max-width: 100%;
  width: calc(100% - 210px);
  height: 100%;
`;

const Slide = styled.div<{ slide: number }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  font-family: 'Roboto';
  ${({ slide }) =>
    slide === 3
      ? `
      font-size: 1rem;
      padding: 0 125px 0 0;
    `
      : `
      font-size: 1.33rem;
      padding: 0 125px;
    `}

  row-gap: 40px;
  animation: fadeInRightBig 0.5s;
  height: 100%;
`;

const StepTitle = styled.div`
  display: flex;
  max-width: 596px;
  font-family: 'Nunito';
  font-size: 4.1rem;
  font-weight: 700;
  line-height: 100%;
  letter-spacing: -0.011em;
`;

const Content = styled.div`
  display: flex;
  max-width: 863px;
  line-height: 26px;
  font-family: 'Roboto';
  font-weight: 400;
  white-space: pre-line;
  button {
    margin-top: 20px;
  }
`;
const ErrorMessage = styled.div`
  color: red;
`;

const SpinnerContainer = styled.div<{ slide: number }>`
  margin-left: 15px;
  ${({ slide }) =>
    slide === 3 &&
    `
    position: absolute;
    bottom: 7%;
    margin-left: 45px;
    left: 410px;
    `}
`;

// ------------

const QuestionsContainer = styled.div`
  max-height: 100%;
  display: flex;
  flex-direction: column;
  gap: 3rem;
  padding: 20px 30px 20px 70px;
  height: calc(100% - 11rem);
  width: calc(100% - 85px);
  overflow-y: auto;

  ::-webkit-scrollbar {
    transition: width 0.2s;
    width: 4px;
    height: 6px;

    &:hover {
      width: 6px;
    }
  }
`;

// ------------------------------------------------------------------------------

const ListContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  row-gap: 20px;
  overflow-y: auto;
  padding-right: 10px;
  margin-top: 10px;
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  row-gap: 5px;
`;

const Title = styled.span`
  font-weight: 700;
  font-size: 2.6em;
  font-family: 'Nunito', sans-serif;
`;

const SurveyTop = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  padding: 0 70px;
  row-gap: 30px;
`;

const SurveyPageContainer = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
`;

const SurveyContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  padding-top: 40px;
  gap: 1rem;
`;

const ThemeContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 300px;
  min-width: 300px;
  background-color: #f1f8fd;
  padding: 40px;
  border-left: 5px solid white;
`;

const ThemeList = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 20px;
  height: 100%;
`;

const ThemeTitle = styled.span`
  font-weight: 700;
  font-size: 2.6em;
  font-family: 'Nunito', sans-serif;
`;

const Theme = styled.div<{ selected: boolean; completed: boolean }>`
  background-color: white;
  border-radius: 10px;
  padding: 5px 10px;
  font-size: 1.2em;
  font-weight: 400;
  font-family: 'Roboto', serif;
  display: flex;
  align-items: center;
  column-gap: 5px;
  border: 1px solid rgba(0, 0, 0, 0.2);
  transition: all 0.2s ease-in-out;
  white-space: nowrap;
  height: 25px;

  ${({ completed }) =>
    completed &&
    `
    border: 1px solid transparent;
    background-color: ${COLORS.DarkGreen};
    color: white;
  `};

  ${({ selected }) =>
    selected &&
    `
    background-color: ${COLORS.NiceBlue};
    color: white;
  `};

  &:hover {
    background-color: ${COLORS.NiceBlue};
    color: white;
    cursor: pointer;
  }

  svg {
    width: 24px;
    height: 24px;
  }

  span {
    flex: 1;
  }

  .share {
    align-self: flex-start;
    opacity: 0.5;
    transition: opacity 0.3s;

    &:hover {
      opacity: 1;
    }
  }
`;

const Circle = styled.div`
  background-color: rgba(200, 200, 200, 1);
  border-radius: 50px;
  width: 20px;
  height: 20px;
  min-width: 20px;
  min-height: 20px;
  max-width: 20px;
  max-height: 20px;
  margin-right: 4px;
`;
